import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, UserCheck, Settings, GraduationCap, Calendar, BarChart3, ShieldCheck, CheckSquare, Sparkles } from "lucide-react";

export default function SaasDashboardPreview() {
  const [activeDashboard, setActiveDashboard] = useState("student");

  const dashboards = {
    student: {
      name: "Student Portal",
      desc: "Gamified, mobile-responsive layout where students access study streams, ask the AI Tutor questions, take tests, and track their rankings.",
      color: "from-violet-500 to-indigo-500",
      content: (
        <div className="bg-slate-950 h-full w-full flex flex-col p-4 text-white">
          {/* Top Navbar */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-violet-600/30 flex items-center justify-center text-violet-400">
                <GraduationCap size={16} />
              </div>
              <div>
                <p className="text-xs font-black">Rohan Kumar</p>
                <p className="text-[8px] text-slate-500 font-bold">Standard 10 - Science Stream</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-400 text-[8px] font-black border border-emerald-900">
                Rank #4
              </div>
              <span className="text-[10px] text-slate-400">⚡ 120 Streak</span>
            </div>
          </div>

          {/* Dash Body */}
          <div className="flex-1 grid grid-cols-12 gap-3 overflow-hidden">
            {/* Main column */}
            <div className="col-span-8 space-y-3">
              {/* Active Courses */}
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">My Live Syllabus</p>
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-[8px] font-bold mb-1">
                      <span>Intro to Thermodynamics</span>
                      <span className="text-violet-400">85% Complete</span>
                    </div>
                    <div className="w-full h-1 bg-slate-800 rounded-full">
                      <div className="h-full bg-violet-500 rounded-full w-[85%]" />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-[8px] font-bold mb-1">
                      <span>Organic Chemistry Basics</span>
                      <span className="text-blue-400">42% Complete</span>
                    </div>
                    <div className="w-full h-1 bg-slate-800 rounded-full">
                      <div className="h-full bg-blue-500 rounded-full w-[42%]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Doubt solver quick panel */}
              <div className="bg-gradient-to-r from-violet-900/30 to-blue-900/30 rounded-xl p-3 border border-violet-500/20 flex gap-2">
                <div className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center shrink-0">
                  <Sparkles size={12} />
                </div>
                <div>
                  <p className="text-[8px] font-black">AI Tutor Recommendation</p>
                  <p className="text-[7px] text-slate-300 mt-0.5 leading-relaxed">
                    "You struggle with adiabatic expansions. Try the 5-minute quiz to reinforce concepts."
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar widgets */}
            <div className="col-span-4 space-y-3">
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Upcoming Exam</p>
                <p className="text-[9px] font-black text-white leading-tight">Advanced Calculus</p>
                <p className="text-[7px] text-slate-500 mt-1">Tomorrow, 10:00 AM</p>
                <button className="w-full mt-2 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded text-[7px] font-bold transition-colors">
                  Prepare Now
                </button>
              </div>

              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 text-center">
                <p className="text-[7px] text-slate-400 font-bold">Certificates Earned</p>
                <p className="text-base font-black text-amber-500 mt-0.5">3</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    teacher: {
      name: "Teacher Dashboard",
      desc: "Comprehensive workspace allowing educators to build online question papers, evaluate written scripts using AI assistance, and monitor students.",
      color: "from-blue-500 to-indigo-500",
      content: (
        <div className="bg-slate-950 h-full w-full flex flex-col p-4 text-white">
          {/* Top Navbar */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-600/30 flex items-center justify-center text-blue-400">
                <UserCheck size={16} />
              </div>
              <div>
                <p className="text-xs font-black">Dr. Shreya Mehta</p>
                <p className="text-[8px] text-slate-500 font-bold">HOD - Physics Department</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-[7px] font-bold transition-colors flex items-center gap-1">
                + Create Exam
              </button>
            </div>
          </div>

          {/* Dash Body */}
          <div className="flex-1 grid grid-cols-12 gap-3 overflow-hidden">
            {/* Left Main */}
            <div className="col-span-8 space-y-3">
              {/* Submissions Pending */}
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-[9px] font-bold text-slate-400 uppercase">Evaluations Pending</p>
                  <span className="text-[8px] text-amber-400 font-bold">12 papers left</span>
                </div>
                <div className="space-y-1.5 text-[8px]">
                  <div className="flex justify-between items-center bg-slate-850 p-1.5 rounded border border-slate-800/40">
                    <span>Aarav Saxena - Term Physics</span>
                    <button className="px-2 py-0.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-[7px]">
                      Evaluate (AI Assist)
                    </button>
                  </div>
                  <div className="flex justify-between items-center bg-slate-850 p-1.5 rounded border border-slate-800/40">
                    <span>Neha Sharma - Kinetics MCQ</span>
                    <span className="text-emerald-400 font-bold">AI Auto-Graded</span>
                  </div>
                </div>
              </div>

              {/* Class Attendance */}
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Weekly Attendance Avg</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-black text-white">94.8%</span>
                  <span className="text-[7px] text-slate-500">vs 92% school average</span>
                </div>
              </div>
            </div>

            {/* Right sidebar */}
            <div className="col-span-4 space-y-3">
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 text-center">
                <p className="text-[7px] text-slate-400 font-bold uppercase">Active Classes</p>
                <p className="text-xl font-black text-white mt-1">4</p>
                <p className="text-[7px] text-slate-500">2 live lectures today</p>
              </div>

              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                <p className="text-[8px] text-slate-400 font-bold uppercase mb-1.5">AI Exam Assistant</p>
                <div className="p-1.5 bg-slate-850 rounded border border-slate-800/40 text-[7px] text-slate-300">
                  "Auto-generated 15 MCQ questions on Electromagnetic Induction."
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    institute: {
      name: "Institute Admin Portal",
      desc: "High-level overview interface for institution owners to review registration trends, manage branding settings, monitor billing logs, and control tenants.",
      color: "from-cyan-500 to-blue-500",
      content: (
        <div className="bg-slate-950 h-full w-full flex flex-col p-4 text-white">
          {/* Top Navbar */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-cyan-600/30 flex items-center justify-center text-cyan-400">
                <Settings size={16} />
              </div>
              <div>
                <p className="text-xs font-black">Skillzy Platform Admin</p>
                <p className="text-[8px] text-slate-500 font-bold">Consolidated Management Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[8px] text-slate-400">
              <span>Tenant Uptime: 99.98%</span>
            </div>
          </div>

          {/* Dash Body */}
          <div className="flex-1 grid grid-cols-12 gap-3 overflow-hidden">
            {/* Main Stats panel */}
            <div className="col-span-8 space-y-3">
              {/* Platform metrics */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                  <p className="text-[7px] font-bold text-slate-400 uppercase">Active Subdomains</p>
                  <p className="text-base font-black text-white mt-1">104</p>
                  <span className="text-[6px] text-emerald-400 font-bold">+4 this week</span>
                </div>
                <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                  <p className="text-[7px] font-bold text-slate-400 uppercase">LMS Monthly Traffic</p>
                  <p className="text-base font-black text-white mt-1">4.2 Million</p>
                  <span className="text-[6px] text-slate-500">Hits audited</span>
                </div>
              </div>

              {/* Consolidated billing */}
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                <div className="flex justify-between items-center mb-1">
                  <p className="text-[8px] font-bold text-slate-400 uppercase">Platform Subscriptions</p>
                  <span className="text-[7px] text-cyan-400">MRR: ₹3,42,500</span>
                </div>
                <div className="w-full h-8 flex items-end gap-1.5 pt-2">
                  <div className="w-full h-[30%] bg-cyan-500/20 rounded-t" />
                  <div className="w-full h-[45%] bg-cyan-500/30 rounded-t" />
                  <div className="w-full h-[60%] bg-cyan-500/50 rounded-t" />
                  <div className="w-full h-[75%] bg-cyan-500/70 rounded-t" />
                  <div className="w-full h-[95%] bg-cyan-500 rounded-t" />
                </div>
              </div>
            </div>

            {/* Sidebar widgets */}
            <div className="col-span-4 space-y-3">
              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                <p className="text-[7px] font-bold text-slate-400 uppercase">Security Check</p>
                <div className="mt-1 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-[7px] font-black text-slate-200">SOC2 Verified</span>
                </div>
                <div className="mt-1 text-[6px] text-slate-500">No database leaks or isolation threats detected.</div>
              </div>

              <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 text-center">
                <p className="text-[7px] text-slate-400 font-bold uppercase">Total Tenants</p>
                <p className="text-lg font-black text-white mt-0.5">32 Schools</p>
                <p className="text-[7px] text-slate-500">72 Coaching Inst.</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  };

  return (
    <section id="solutions-preview" className="py-24 bg-white dark:bg-slate-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            See the Platform in Action
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Toggle between the specialized client views of the Skillzy LMS. Our interfaces are designed for modern classrooms and simple administrative overhead.
          </p>
        </div>

        {/* Dashboards tab selectors */}
        <div className="flex justify-center gap-4 mb-10">
          {Object.keys(dashboards).map((key) => {
            const isSelected = activeDashboard === key;
            return (
              <button
                key={key}
                onClick={() => setActiveDashboard(key)}
                className={`px-5 py-2.5 rounded-full text-xs font-black transition-all border ${
                  isSelected
                    ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-md"
                    : "bg-slate-50 dark:bg-slate-900/50 border-slate-100 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:border-slate-200 dark:hover:border-slate-700"
                }`}
              >
                {dashboards[key].name}
              </button>
            );
          })}
        </div>

        {/* Laptop Container Mockup */}
        <div className="max-w-4xl mx-auto relative">
          
          {/* Screen Bezel */}
          <div className="bg-slate-900 p-3 sm:p-4 rounded-t-[2rem] border-t border-l border-r border-slate-700/80 shadow-2xl relative">
            {/* Screen Notch camera */}
            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-16 h-3 bg-slate-950 rounded-b-lg flex items-center justify-center gap-1 z-20">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
              <div className="w-1 h-1 rounded-full bg-blue-900/50 animate-pulse" />
            </div>

            {/* Screen Content */}
            <div className="aspect-[16/10] bg-slate-950 rounded-lg overflow-hidden border border-slate-800 relative z-10">
              
              {/* Fade in content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeDashboard}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="w-full h-full"
                >
                  {dashboards[activeDashboard].content}
                </motion.div>
              </AnimatePresence>

            </div>
          </div>

          {/* Laptop Base (Keyboard region) */}
          <div className="bg-slate-800 h-4 rounded-b-[1.5rem] border-b-4 border-slate-900 shadow-2xl relative z-20">
            {/* Notch indent */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1.5 bg-slate-900 rounded-b-md" />
          </div>

          {/* Trackpad area shadow */}
          <div className="w-11/12 mx-auto h-2 bg-slate-900/10 dark:bg-black/50 blur-sm rounded-full" />

        </div>

      </div>
    </section>
  );
}
